// EINGEFROREN 30.10.2026 (Ballast-Abbau P8), bei Bedarf reaktivieren: Route ausgeblendet bzw. API-Weg 404 (`FROZEN_TENANT_ROUTES`).
/**
 * Galerie bearbeiten (Stufe D, Teil 2): Einstellungen, normale Regeln als Gesamtliste und Neuanlage.
 * Prüfungen laufen mit denselben Funktionen wie in der API (`shared/`); gespeichert wird mit
 * Versionsvergleich, ein Konflikt meldet „inzwischen geändert“. Skripte bleiben lesend.
 */
import {
  Alert,
  Button,
  Card,
  Checkbox,
  ColorInput,
  Group,
  NumberInput,
  Select,
  Stack,
  Switch,
  TagsInput,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import type { Gallery } from '@pat/core/entities';
import type { NormalRule, SearchExpr } from '@pat/core/rules';
import { GallerySettingsInput, type GalleryDetailResponse, type RulesDraft } from '../../../shared/galleries.js';
import { RULE_KINDS, ruleIssue, ruleLimit, ruleShape } from '../../../shared/rule-input.js';
import { ALL_EXPR, formatSearchText, hasUnsupported, isAllExpr, parseSearchText } from '../../../shared/search-text.js';
import { ApiRequestError } from '../api/client.js';
import { EBAY_SITES } from '../sites.js';
import { t } from '../texts.js';
import { useServices } from '../services.js';

type Detail = GalleryDetailResponse;
type GallerySettings = Gallery['settings'];

function errorText(error: unknown): string {
  return error instanceof ApiRequestError && error.detail ? error.detail : t('common.saveFailed');
}

// ---------------------------------------------------------------------------
// Einstellungen
// ---------------------------------------------------------------------------

const COLOR_KEYS = ['c1', 'c3', 'i1', 'i2', 'i3'] as const;
const DEFAULT_COLORS = { c1: 'ffffff', c3: 'cccccc', i1: 'ffffff', i2: '333333', i3: 'cccccc' };

/** Zahlenfelder mit Bereich; leer ist bei Pflichtfeldern ein Fehler, bei optionalen „automatisch“. */
const NUMBER_FIELDS = {
  maxItems: { min: 1, max: 100, required: true },
  height: { min: 1, max: 9, required: false },
  titleLines: { min: 0, max: 4, required: true },
  width: { min: 100, max: 2000, required: false },
} as const;
type NumberField = keyof typeof NUMBER_FIELDS;

function fieldIssue(settings: GallerySettings): string | undefined {
  for (const [field, range] of Object.entries(NUMBER_FIELDS) as [NumberField, (typeof NUMBER_FIELDS)[NumberField]][]) {
    const value = settings[field];
    if (value === undefined && !range.required) continue;
    if (value === undefined || !Number.isInteger(value) || value < range.min || value > range.max) {
      return t('galleries.edit.range', { field: t(`galleries.${field}`), min: range.min, max: range.max });
    }
  }
  return undefined;
}

/** Farbfeld mit eigenem Text: Zwischenstände beim Tippen bleiben erhalten, gespeichert wird nur gültiges Hex (auch im Designeditor). */
export function ColorField({ label, value, onValid, onValidity, disabled }: {
  label: ReactNode;
  value: string;
  onValid: (hex: string) => void;
  onValidity: (valid: boolean) => void;
  disabled?: boolean;
}) {
  const [text, setText] = useState(`#${value}`);
  const valid = /^#?[0-9a-fA-F]{6}$/.test(text);
  return (
    <ColorInput
      label={label}
      format="hex"
      value={text}
      disabled={disabled ?? false}
      error={valid ? undefined : t('galleries.colors.invalid')}
      onChange={next => {
        setText(next);
        const hex = next.replace(/^#/, '');
        const ok = /^[0-9a-fA-F]{6}$/.test(hex);
        onValidity(ok);
        if (ok) onValid(hex.toLowerCase());
      }}
    />
  );
}

export function GallerySettingsForm({ tenantId, detail, onDone }: { tenantId: string; detail: Detail; onDone: () => void }) {
  const { api } = useServices();
  const queryClient = useQueryClient();
  // Stand beim Öffnen: gespeichert wird gegen genau diese Version, sonst Konflikt statt stillem Überschreiben.
  const [baseVersion] = useState(detail.gallery.version);
  const [name, setName] = useState(detail.gallery.name);
  const [settings, setSettings] = useState<GallerySettings>(detail.gallery.settings);
  // Farbfelder mit ungültigem Zwischenstand (Speichern gesperrt, bis sie stimmen).
  const [invalidColors, setInvalidColors] = useState<ReadonlySet<string>>(new Set());
  const set = <K extends keyof GallerySettings>(key: K, value: GallerySettings[K]) => setSettings(current => ({ ...current, [key]: value }));
  const parsed = GallerySettingsInput.safeParse(settings);
  const issue = !name.trim()
    ? t('galleries.edit.nameRequired')
    : (fieldIssue(settings) ?? (parsed.success ? undefined : (parsed.error.issues.find(i => i.code === 'custom')?.message ?? t('galleries.edit.invalid'))));
  const save = useMutation({
    mutationFn: () => api.saveGallery(tenantId, detail.gallery.galleryId, { expectedVersion: baseVersion, name: name.trim(), settings }),
    onSuccess: saved => {
      queryClient.setQueryData(['gallery', tenantId, detail.gallery.galleryId], saved);
      void queryClient.invalidateQueries({ queryKey: ['galleries', tenantId] });
      onDone();
    },
  });
  // Leer: optionale Felder „automatisch“, Pflichtfelder bleiben leer und zeigen einen Fehler.
  const number = (key: NumberField, value: string | number) => set(key, (value === '' || typeof value !== 'number' ? undefined : value) as never);
  return (
    <Stack gap="sm">
      <TextInput
        label={t('galleries.col.name')}
        value={name}
        onChange={e => setName(e.currentTarget.value)}
        maxLength={120}
        required
        // Der Name steht im Marker des Einbettcodes (Legacy, Blockmodus): Umbenennen revidiert alle Einbettungen (Claude 25.10.2026).
        description={name.trim() && name.trim() !== detail.gallery.name ? t('galleries.edit.renameWarning') : undefined}
      />
      <Group grow>
        <NumberInput label={t('galleries.maxItems')} min={1} max={100} allowDecimal={false} value={settings.maxItems ?? ''} onChange={v => number('maxItems', v)} />
        <NumberInput label={t('galleries.height')} min={1} max={9} allowDecimal={false} value={settings.height ?? ''} placeholder="2" onChange={v => number('height', v)} />
        <NumberInput label={t('galleries.titleLines')} min={0} max={4} allowDecimal={false} value={settings.titleLines ?? ''} onChange={v => number('titleLines', v)} />
      </Group>
      <Group grow>
        <NumberInput label={t('galleries.width')} placeholder={t('galleries.width.auto')} min={100} max={2000} allowDecimal={false} value={settings.width ?? ''} onChange={v => number('width', v)} />
        <Select
          label={t('galleries.language')}
          data={['de', 'en', 'fr', 'it'].map(value => ({ value, label: value.toUpperCase() }))}
          value={settings.language}
          onChange={v => v && set('language', v as GallerySettings['language'])}
          allowDeselect={false}
        />
      </Group>
      <Group grow>
        <Select
          label={t('galleries.sort')}
          data={[0, 1, 2, 3].map(value => ({ value: String(value), label: t(SORT_KEYS[value]!) }))}
          value={String([1, 2, 3].includes(settings.sortMode) ? settings.sortMode : 0)}
          onChange={v => v && set('sortMode', Number(v))}
          allowDeselect={false}
        />
        <Select
          label={t('galleries.linkTarget')}
          data={[
            { value: '_blank', label: t('galleries.linkTarget._blank') },
            { value: '_self', label: t('galleries.linkTarget._self') },
          ]}
          value={settings.linkTarget}
          onChange={v => v && set('linkTarget', v as GallerySettings['linkTarget'])}
          allowDeselect={false}
        />
      </Group>
      <Switch label={t('galleries.random')} checked={settings.randomize} onChange={e => set('randomize', e.currentTarget.checked)} />
      <Switch label={t('galleries.showPrice')} checked={settings.showPrice} onChange={e => set('showPrice', e.currentTarget.checked)} />
      <Switch label={t('galleries.showInfoText')} checked={settings.showInfoText} onChange={e => set('showInfoText', e.currentTarget.checked)} />
      <Switch label={t('galleries.showAuctionType')} checked={settings.showAuctionType} onChange={e => set('showAuctionType', e.currentTarget.checked)} />
      <Switch label={t('galleries.bigPicture')} checked={settings.bigPicture ?? false} onChange={e => set('bigPicture', e.currentTarget.checked)} />
      <Switch label={t('galleries.sameSiteOnly')} checked={settings.sameSiteOnly ?? true} onChange={e => set('sameSiteOnly', e.currentTarget.checked)} />
      <Checkbox
        label={t('galleries.colors.own')}
        checked={settings.colors !== undefined}
        onChange={e => {
          const own = e.currentTarget.checked;
          setInvalidColors(new Set());
          setSettings(({ colors: _colors, ...rest }) => (own ? { ...rest, colors: DEFAULT_COLORS } : rest));
        }}
      />
      {settings.colors && (
        <Group grow>
          {COLOR_KEYS.map(key => (
            <ColorField
              key={key}
              label={t(`galleries.colors.${key}`)}
              value={settings.colors![key]}
              onValid={hex => setSettings(current => (current.colors ? { ...current, colors: { ...current.colors, [key]: hex } } : current))}
              onValidity={valid =>
                setInvalidColors(current => {
                  const next = new Set(current);
                  if (valid) next.delete(key);
                  else next.add(key);
                  return next;
                })
              }
            />
          ))}
        </Group>
      )}
      {issue && (
        <Text size="sm" c="red" role="alert">
          {issue}
        </Text>
      )}
      {save.isError && (
        <Alert color="red" role="alert">
          {errorText(save.error)}
        </Alert>
      )}
      <Group>
        <Button onClick={() => save.mutate()} loading={save.isPending} disabled={issue !== undefined || invalidColors.size > 0}>
          {t('common.save')}
        </Button>
        <Button variant="default" onClick={onDone}>
          {t('common.cancel')}
        </Button>
      </Group>
    </Stack>
  );
}

const SORT_KEYS: Record<number, 'galleries.sort.none' | 'galleries.sort.newest' | 'galleries.sort.priceAsc' | 'galleries.sort.priceDesc'> = {
  0: 'galleries.sort.none',
  1: 'galleries.sort.newest',
  2: 'galleries.sort.priceAsc',
  3: 'galleries.sort.priceDesc',
};

// ---------------------------------------------------------------------------
// Regeln
// ---------------------------------------------------------------------------

interface SideState {
  text: string;
  all: boolean;
}

export interface RuleRow {
  ruleId: string;
  kind: NormalRule['kind'];
  priority: number;
  categories: string[];
  source: SideState;
  target: SideState;
  /** Unverändert zu übernehmen: enthält nicht übersetzbare Altbegriffe. */
  frozen?: NormalRule;
  legacyRuleId?: string;
}

function side(expr: SearchExpr | undefined): SideState {
  return { text: formatSearchText(expr) ?? '', all: isAllExpr(expr) };
}

export function rowOf(rule: NormalRule): RuleRow {
  const frozen = hasUnsupported(rule.source) || hasUnsupported(rule.target);
  return {
    ruleId: rule.ruleId,
    kind: rule.kind,
    priority: rule.priority,
    categories: rule.categoryIds ?? [],
    source: side(rule.source),
    target: side(rule.target),
    ...(frozen ? { frozen: rule } : {}),
    ...(rule.legacyRuleId ? { legacyRuleId: rule.legacyRuleId } : {}),
  };
}

function sideExpr(state: SideState, optional = false): { expr?: SearchExpr; error?: string } {
  if (state.all) return { expr: ALL_EXPR };
  // Titelsuche ohne Ausgang: gilt für jedes Ausgangsangebot (wie in der Engine).
  if (optional && !state.text.trim()) return {};
  const parsed = parseSearchText(state.text);
  return parsed.ok ? { expr: parsed.expr } : { error: parsed.error };
}

/** Zeile → Regel oder verständlicher Fehler (dieselbe Prüfung wie die API). */
export function ruleOf(row: RuleRow): { rule?: NormalRule; error?: string } {
  if (row.frozen) return { rule: row.frozen };
  if (!Number.isInteger(row.priority) || row.priority < 0 || row.priority > 9) return { error: t('galleries.rules.priorityRange') };
  const base = { ruleId: row.ruleId, kind: row.kind, priority: row.priority, ...(row.legacyRuleId ? { legacyRuleId: row.legacyRuleId } : {}) };
  const shape = ruleShape(row.kind);
  let rule: NormalRule = base;
  if (shape === 'categories') rule = { ...base, categoryIds: row.categories };
  if (shape === 'search') {
    const source = sideExpr(row.source, row.kind === 'title-search');
    if (source.error) return { error: `${t('galleries.rules.source')}: ${source.error}` };
    const target = sideExpr(row.target);
    if (target.error) return { error: `${t('galleries.rules.target')}: ${target.error}` };
    rule = { ...base, ...(source.expr ? { source: source.expr } : {}), target: target.expr! };
  }
  const issue = ruleIssue(rule);
  return issue ? { error: issue } : { rule };
}

let ruleCounter = 0;
function newRuleId(): string {
  ruleCounter += 1;
  return `r-${Date.now().toString(36)}-${ruleCounter}`;
}

function SideInput({ label, description, state, onChange }: { label: string; description?: string; state: SideState; onChange: (state: SideState) => void }) {
  return (
    <Stack gap={4} style={{ flex: 1 }}>
      <Textarea
        label={label}
        description={description}
        autosize
        minRows={2}
        maxRows={8}
        value={state.text}
        disabled={state.all}
        placeholder={t('galleries.rules.searchPlaceholder')}
        onChange={e => onChange({ ...state, text: e.currentTarget.value })}
      />
      <Checkbox size="xs" label={t('galleries.rules.all')} checked={state.all} onChange={e => onChange({ ...state, all: e.currentTarget.checked })} />
    </Stack>
  );
}

function RuleRowEditor({ row, index, onChange, onRemove }: { row: RuleRow; index: number; onChange: (row: RuleRow) => void; onRemove: () => void }) {
  const result = ruleOf(row);
  const shape = ruleShape(row.kind);
  return (
    <Card withBorder padding="sm" aria-label={t('galleries.rules.rowLabel', { n: index + 1 })}>
      <Stack gap="xs">
        <Group align="end">
          <Select
            label={t('galleries.ruleKind')}
            data={RULE_KINDS.map(kind => ({ value: kind, label: t(`rules.kind.${kind}`) }))}
            value={row.kind}
            disabled={row.frozen !== undefined}
            allowDeselect={false}
            onChange={kind => kind && onChange({ ...row, kind: kind as NormalRule['kind'] })}
            w={260}
          />
          <NumberInput
            label={t('galleries.priority')}
            min={0}
            max={9}
            w={110}
            allowDecimal={false}
            value={Number.isNaN(row.priority) ? '' : row.priority}
            disabled={row.frozen !== undefined}
            onChange={v => onChange({ ...row, priority: typeof v === 'number' ? v : Number.NaN })}
          />
          <Button variant="subtle" color="red" onClick={onRemove}>
            {t('galleries.rules.remove')}
          </Button>
        </Group>
        {row.frozen ? (
          <Text size="sm" c="dimmed">
            {t('galleries.rules.frozen')}
          </Text>
        ) : shape === 'categories' ? (
          <TagsInput
            label={t('galleries.rules.categories')}
            description={t('galleries.rules.categoriesHint')}
            value={row.categories}
            onChange={categories => onChange({ ...row, categories: categories.map(c => c.trim()).filter(Boolean) })}
            maxTags={50}
          />
        ) : shape === 'search' ? (
          <Group align="start" grow>
            <SideInput
              label={t('galleries.rules.source')}
              {...(row.kind === 'title-search' ? { description: t('galleries.rules.sourceOptional') } : {})}
              state={row.source}
              onChange={source => onChange({ ...row, source })}
            />
            <SideInput label={t('galleries.rules.target')} state={row.target} onChange={target => onChange({ ...row, target })} />
          </Group>
        ) : null}
        {result.error && (
          <Text size="sm" c="red">
            {result.error}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

/**
 * Entwurf aus den Zeilen; `undefined`, solange eine Zeile fehlerhaft ist. Der Entwurf geht in die
 * Vorschau (`draft`) und beim Speichern unverändert an die API.
 */
export function draftOf(rows: RuleRow[], removeScripts: boolean): RulesDraft | undefined {
  const rules: NormalRule[] = [];
  for (const row of rows) {
    const { rule } = ruleOf(row);
    if (!rule) return undefined;
    rules.push(rule);
  }
  return { rules, ...(removeScripts ? { removeScripts: true } : {}) };
}

export function RulesEditor({
  tenantId,
  detail,
  baseVersion,
  rows,
  setRows,
  removeScripts,
  setRemoveScripts,
  onDone,
}: {
  tenantId: string;
  detail: Detail;
  /** Regelstand beim Öffnen des Editors; gespeichert wird gegen genau diese Version. */
  baseVersion: number;
  rows: RuleRow[];
  setRows: (rows: RuleRow[]) => void;
  removeScripts: boolean;
  setRemoveScripts: (value: boolean) => void;
  onDone: () => void;
}) {
  const { api } = useServices();
  const queryClient = useQueryClient();
  const draft = draftOf(rows, removeScripts);
  const save = useMutation({
    mutationFn: () => api.saveRules(tenantId, detail.gallery.galleryId, { expectedVersion: baseVersion, ...draft! }),
    onSuccess: saved => {
      queryClient.setQueryData(['gallery', tenantId, detail.gallery.galleryId], saved);
      void queryClient.invalidateQueries({ queryKey: ['galleries', tenantId] });
      onDone();
    },
  });
  return (
    <Stack gap="sm">
      {detail.rules.sourceMatch === 'legacy-substring' && (
        <Text size="xs" c="dimmed">
          {t('galleries.rules.legacyMatch')}
        </Text>
      )}
      {rows.length === 0 && <Text size="sm">{t('galleries.rules.none')}</Text>}
      {rows.map((row, index) => (
        <RuleRowEditor
          key={row.ruleId}
          row={row}
          index={index}
          onChange={next => setRows(rows.map((r, i) => (i === index ? next : r)))}
          onRemove={() => setRows(rows.filter((_, i) => i !== index))}
        />
      ))}
      <Group>
        <Button
          variant="light"
          disabled={rows.length >= ruleLimit(detail.rules.ast.length)}
          onClick={() =>
            setRows([...rows, { ruleId: newRuleId(), kind: 'title-search', priority: 1, categories: [], source: { text: '', all: false }, target: { text: '', all: false } }])
          }
        >
          {t('galleries.rules.add')}
        </Button>
      </Group>
      {detail.rules.scripts.length > 0 && (
        <Checkbox
          label={t('galleries.rules.removeScripts')}
          description={detail.rules.locked ? t('galleries.rules.removeScriptsLocked') : undefined}
          checked={removeScripts}
          onChange={e => setRemoveScripts(e.currentTarget.checked)}
        />
      )}
      {save.isError && (
        <Alert color="red" role="alert">
          {errorText(save.error)}
        </Alert>
      )}
      <Group>
        <Button onClick={() => save.mutate()} loading={save.isPending} disabled={draft === undefined}>
          {t('galleries.rules.save')}
        </Button>
        <Button variant="default" onClick={onDone}>
          {t('common.cancel')}
        </Button>
      </Group>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Neuanlage
// ---------------------------------------------------------------------------

function newGalleryId(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return `gal-${Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')}`;
}

export function CreateGalleryForm({ tenantId, onCancel }: { tenantId: string; onCancel: () => void }) {
  const { api } = useServices();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [site, setSite] = useState<string>('77');
  // Eine Kennung je Anlegevorgang: ein erneuter Klick nach unklarem Ausgang legt keine zweite Galerie an.
  const [galleryId] = useState(newGalleryId);
  const create = useMutation({
    mutationFn: () => api.createGallery(tenantId, { galleryId, name: name.trim(), site: Number(site) }),
    onSuccess: created => {
      void queryClient.invalidateQueries({ queryKey: ['galleries', tenantId] });
      void navigate({ to: '/t/$tenantId/galleries/$galleryId', params: { tenantId, galleryId: created.galleryId } });
    },
  });
  return (
    <Card withBorder padding="lg">
      <Stack gap="sm">
        <Title order={4}>{t('galleries.create.title')}</Title>
        <Text size="sm" c="dimmed">
          {t('galleries.create.hint')}
        </Text>
        <Group align="end">
          <TextInput label={t('galleries.col.name')} value={name} onChange={e => setName(e.currentTarget.value)} maxLength={120} w={320} />
          <Select
            label={t('items.site')}
            data={EBAY_SITES.map(s => ({ value: String(s.id), label: s.label }))}
            value={site}
            onChange={v => v && setSite(v)}
            allowDeselect={false}
            searchable
            w={240}
          />
          <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!name.trim()}>
            {t('galleries.create.submit')}
          </Button>
          <Button variant="default" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        </Group>
        {create.isError && (
          <Alert color="red" role="alert">
            {errorText(create.error)}
          </Alert>
        )}
      </Stack>
    </Card>
  );
}
